import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  createPulseHotTake,
  getPulseErrorMessage,
  getPulsePayload,
  getPulseUploadedImageUrl,
  isPulseApiSuccess,
  listPulseHotTakes,
  uploadPulseImage,
} from '../../services/pulseApi';
import {
  COPY_LIMITS,
  HERO_PRESENTATIONS,
  PULSE_STATUSES,
  adminHotTakeOptionError,
  copyLengthHint,
  datetimeLocalToIso,
  emptyAdminHotTakeForm,
  emptyOption,
  ensureOptionIds,
  formatDeckPosition,
  formatPulseDate,
  normalizeHeroPresentation,
  optionsSummary,
  parseCardPayload,
  parseCardsPayload,
  statusChipColor,
} from './pulseUtils';

const PAGE_SIZE = 30;
const TITLE_MAX = 70;

function PulseHotTakes() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [form, setForm] = useState(emptyAdminHotTakeForm());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const applied = useMemo(
    () => ({
      status: searchParams.get('status') || '',
      offset: Math.max(0, Number(searchParams.get('offset') || 0) || 0),
    }),
    [searchParams]
  );
  const [status, setStatus] = useState(applied.status);

  useEffect(() => {
    setStatus(applied.status);
  }, [applied.status]);

  const writeParams = useCallback(
    (next) => {
      const params = {};
      if (next.status) params.status = next.status;
      if (next.offset) params.offset = String(next.offset);
      setSearchParams(params);
    },
    [setSearchParams]
  );

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await listPulseHotTakes({
        limit: PAGE_SIZE,
        offset: applied.offset,
        status: applied.status || undefined,
      });
      if (!isPulseApiSuccess(result)) {
        setError(getPulseErrorMessage(result, 'Failed to load admin hot takes.'));
        setItems([]);
        setTotal(0);
        return;
      }
      const parsed = parseCardsPayload(getPulsePayload(result));
      setItems(parsed.items);
      setTotal(parsed.total);
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load admin hot takes.');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [applied]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateOption = (index, field, value) => {
    setForm((prev) => {
      const options = [...prev.options];
      const nextValue =
        field === 'label' ? String(value).slice(0, COPY_LIMITS.optionLabel.max) : value;
      options[index] = { ...options[index], [field]: nextValue };
      return { ...prev, options };
    });
  };

  const addOption = () => {
    setForm((prev) => {
      if (prev.options.length >= 4) return prev;
      // 2 or 4 only — jumping from 2 adds the pair so the form never stops on 3.
      const extras = prev.options.length === 2 ? [emptyOption(), emptyOption()] : [emptyOption()];
      return { ...prev, options: [...prev.options, ...extras] };
    });
  };

  const removeOption = (index) => {
    setForm((prev) => {
      if (prev.options.length <= 2) return prev;
      return { ...prev, options: prev.options.filter((_, i) => i !== index) };
    });
  };

  const handleUploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const result = await uploadPulseImage(file);
      if (!isPulseApiSuccess(result)) {
        setError(getPulseErrorMessage(result, 'Failed to upload image.'));
        return;
      }
      const url = getPulseUploadedImageUrl(result);
      if (!url) {
        setError('Upload finished but image URL was missing.');
        return;
      }
      updateField('image_url', url);
      setSnackbar({ open: true, message: 'Image uploaded. Create the hot take to save it.' });
    } catch (uploadError) {
      setError(uploadError.message || 'Failed to upload image.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    const title = form.title.trim().slice(0, TITLE_MAX);
    if (!title) {
      setError('Title is required. It is the question fans vote on.');
      return;
    }
    const labeled = form.options.filter((option) => option.label.trim() || option.id.trim());
    const optionError = adminHotTakeOptionError(labeled);
    if (optionError) {
      setError(optionError);
      return;
    }
    const options = ensureOptionIds(labeled);

    const payload = {
      title,
      options,
      reward_fe: form.reward_fe === '' ? 5 : Number(form.reward_fe) || 0,
      entry_fee_fe: Number(form.entry_fee_fe) || 0,
      status: form.status || 'active',
    };
    if (form.summary.trim()) payload.summary = form.summary.trim();
    if (form.image_url.trim()) payload.image_url = form.image_url.trim();
    if (form.hero_presentation) {
      payload.hero_presentation = normalizeHeroPresentation(form.hero_presentation);
    }
    if (form.hero_aspect_ratio.trim()) {
      payload.hero_aspect_ratio = form.hero_aspect_ratio.trim();
    }
    const starts = datetimeLocalToIso(form.starts_at);
    const deadline = datetimeLocalToIso(form.deadline_at);
    const expires = datetimeLocalToIso(form.expires_at);
    if (starts) payload.starts_at = starts;
    if (deadline) payload.deadline_at = deadline;
    if (expires) payload.expires_at = expires;

    setSaving(true);
    setError('');
    try {
      const result = await createPulseHotTake(payload);
      if (!isPulseApiSuccess(result)) {
        setError(getPulseErrorMessage(result, 'Failed to create hot take.'));
        return;
      }
      const created = parseCardPayload(getPulsePayload(result));
      const savedId = created?.id;
      setSnackbar({ open: true, message: 'Admin hot take created. Any vote pays the reward.' });
      setForm(emptyAdminHotTakeForm());
      if (savedId) {
        setTimeout(() => {
          navigate(`/admin/pulse/cards/${savedId}`);
        }, 600);
      } else {
        loadList();
      }
    } catch (saveError) {
      setError(saveError.message || 'Failed to create hot take.');
    } finally {
      setSaving(false);
    }
  };

  const hasMore = applied.offset + items.length < total;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Admin hot takes
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Standalone opinion cards (<code>source_type: hot_take_admin</code>). These are not the hot
        take glued under a fan story or news card. Title is the question (clipped to 70). Options are
        2 or 4, labels {COPY_LIMITS.optionLabel.soft} characters. There is no correct answer — any
        vote pays the reward immediately. Defaults: reward 5 FE, entry free, window 24h, status
        active. Newest upload leads the next deck cycle. Disable or expire to take one out of
        rotation. Cards are never deleted.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          {PULSE_STATUSES.map((item) => (
            <MenuItem key={item.value || 'all'} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={() => writeParams({ status, offset: 0 })}>
          Apply
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <TableContainer sx={{ mb: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell title="Where this card sits on a fresh app open. — = not on the deck">
                #
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Options</TableCell>
              <TableCell>Reward / Fee</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Expires</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No admin hot takes yet.
                </TableCell>
              </TableRow>
            ) : (
              items.map((card) => (
                <TableRow
                  key={card.id}
                  hover
                  component={Link}
                  to={`/admin/pulse/cards/${encodeURIComponent(card.id)}`}
                  sx={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
                >
                  <TableCell>
                    {formatDeckPosition(card.deck_position ?? card.deckPosition)}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={card.status} color={statusChipColor(card.status)} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {card.title || card.statement || 'Untitled'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {optionsSummary(card)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {card.reward_fe ?? 0} / {card.entry_fee_fe ?? 0} FE
                  </TableCell>
                  <TableCell>{formatPulseDate(card.created_at || card.createdAt)}</TableCell>
                  <TableCell>{formatPulseDate(card.expires_at ?? card.expiresAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Button
          disabled={applied.offset <= 0 || loading}
          onClick={() => writeParams({ ...applied, offset: Math.max(0, applied.offset - PAGE_SIZE) })}
        >
          Previous
        </Button>
        <Typography variant="body2" color="text.secondary">
          {total === 0
            ? '0 hot takes'
            : `${applied.offset + 1}–${applied.offset + items.length} of ${total}`}
        </Typography>
        <Button
          disabled={!hasMore || loading}
          onClick={() => writeParams({ ...applied, offset: applied.offset + PAGE_SIZE })}
        >
          Next
        </Button>
      </Box>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        New hot take
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Title (the question)"
                value={form.title}
                onChange={(e) => updateField('title', e.target.value.slice(0, TITLE_MAX))}
                margin="dense"
                helperText={`${form.title.length}/${TITLE_MAX} — clipped to 70. No correct answer.`}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Status"
                value={form.status}
                onChange={(e) => updateField('status', e.target.value)}
                margin="dense"
              >
                <MenuItem value="active">Active (publish)</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Summary (optional)"
                value={form.summary}
                onChange={(e) => updateField('summary', e.target.value)}
                margin="dense"
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Image URL (optional)"
                value={form.image_url}
                onChange={(e) => updateField('image_url', e.target.value)}
                margin="dense"
                helperText="HTTPS CDN URL, or upload. Empty → no photo."
              />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading…' : 'Upload'}
                  <input
                    ref={fileInputRef}
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handleUploadImage}
                  />
                </Button>
              </Box>
              {form.image_url ? (
                <Box
                  component="img"
                  src={form.image_url}
                  alt=""
                  sx={{ mt: 1, maxHeight: 120, maxWidth: 240, objectFit: 'cover', borderRadius: 1 }}
                />
              ) : null}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Hero presentation"
                value={form.hero_presentation}
                onChange={(e) => updateField('hero_presentation', e.target.value)}
                margin="dense"
                helperText="Omit → app bleed"
              >
                {HERO_PRESENTATIONS.map((item) => (
                  <MenuItem key={item.value || 'default'} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Hero aspect ratio"
                value={form.hero_aspect_ratio}
                onChange={(e) => updateField('hero_aspect_ratio', e.target.value)}
                margin="dense"
                placeholder="4:3"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Reward FE"
                value={form.reward_fe}
                onChange={(e) => updateField('reward_fe', e.target.value)}
                margin="dense"
                helperText="Default 5"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Entry fee FE"
                value={form.entry_fee_fe}
                onChange={(e) => updateField('entry_fee_fe', e.target.value)}
                margin="dense"
                helperText="Default free"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Starts at"
                value={form.starts_at}
                onChange={(e) => updateField('starts_at', e.target.value)}
                margin="dense"
                InputLabelProps={{ shrink: true }}
                helperText="Empty → now, window +24h"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Deadline at"
                value={form.deadline_at}
                onChange={(e) => updateField('deadline_at', e.target.value)}
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Expires at"
                value={form.expires_at}
                onChange={(e) => updateField('expires_at', e.target.value)}
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Options (2 or 4)
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addOption}
              size="small"
              disabled={form.options.length >= 4}
            >
              {form.options.length === 2 ? 'Add two options' : 'Add option'}
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Leave Agree / Disagree, or write 2 or 4 labels ({COPY_LIMITS.optionLabel.soft} characters).
            No correct answer.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Label</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {form.options.map((option, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={option.id}
                      onChange={(e) => updateOption(index, 'id', e.target.value)}
                      placeholder="yes"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={option.label}
                      onChange={(e) => updateOption(index, 'label', e.target.value)}
                      helperText={copyLengthHint('optionLabel', option.label)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => removeOption(index)}
                      disabled={form.options.length <= 2}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 1, mb: 4 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Creating…' : 'Create hot take'}
        </Button>
        <Button component={Link} to="/admin/pulse">
          Cancel
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
}

export default PulseHotTakes;
